import memoize from 'memoizee';
import * as request from 'superagent';
import { OneOrMore } from 'types';
import { asArray } from 'utils/collections';

import { getContext } from './audio-context';
import { AudioSegment, IAudioSource, IAudioSourceConfig } from './types';

export function calculateBufferSize(millis: number, sampleRate: number) {
  return Math.ceil((millis * sampleRate) / 1000);
}

function blobToArrayBuffer(blob: Blob) {
  const reader = new FileReader();
  return new Promise<ArrayBuffer>(resolve => {
    reader.onloadend = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(blob);
  });
}

function fetchAudioData(url: string) {
  return request
    .get(url)
    .responseType('blob')
    .then(res => blobToArrayBuffer(res.body));
}

function decodeAudioDataPromise(ctx: AudioContext, data: ArrayBuffer) {
  return new Promise<AudioBuffer>((resolve, reject) => {
    ctx.decodeAudioData(
      data,
      audioBuffer => resolve(audioBuffer),
      err => reject(err),
    );
  });
}

export function startRendering(ctx: OfflineAudioContext) {
  return new Promise<AudioBuffer>(resolve => {
    ctx.oncomplete = (e: OfflineAudioCompletionEvent) =>
      resolve(e.renderedBuffer);
    ctx.startRendering();
  });
}

function loadAudio(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  return url
    ? fetchAudioData(url).then(data => decodeAudioDataPromise(ctx, data))
    : Promise.resolve(undefined);
}

const memoizedLoadAudio = memoize(loadAudio, {
  normalizer(args) {
    return args[1];
  },
  primitive: true,
  promise: true,
  refCounter: true,
} as any);

export function removeAudioSource(url: string) {
  (memoizedLoadAudio as any).deleteRef(null, url);
}

function createSegmentBuffers(
  entireAudioBuffer: AudioBuffer,
  segments: OneOrMore<AudioSegment>,
  ctx: AudioContext = getContext(),
) {
  const buffers = asArray(segments).map(({ startSec, endSec }) => {
    const durationSec = endSec - startSec;
    const firstSample = Math.floor(entireAudioBuffer.sampleRate * startSec);

    const segmentBuffer = ctx.createBuffer(
      entireAudioBuffer.numberOfChannels,
      durationSec * entireAudioBuffer.sampleRate,
      entireAudioBuffer.sampleRate,
    );

    for (let i = 0; i < entireAudioBuffer.numberOfChannels; i += 1) {
      segmentBuffer.copyToChannel(
        entireAudioBuffer
          .getChannelData(i)
          .slice(firstSample, firstSample + segmentBuffer.length),
        i,
      );
    }

    return segmentBuffer;
  });

  return buffers;
}

export async function createAudioSource<
  T extends Pick<IAudioSourceConfig, 'segments' | 'source'>
>(
  config: T,
  ctx: AudioContext = getContext(),
): Promise<T & { buffers: AudioBuffer[] }> {
  const { segments, source } = config;

  const entireAudioBuffer =
    source instanceof AudioBuffer
      ? source
      : await memoizedLoadAudio(ctx, source);

  if (!segments) {
    return { ...config, buffers: [entireAudioBuffer] };
  }

  return {
    ...config,
    buffers: createSegmentBuffers(entireAudioBuffer, segments, ctx),
  };
}

export function setGainAtTime(
  gain: AudioParam,
  value: number,
  time: number,
  immediate?: boolean,
) {
  if (time < 0) return;
  if (immediate) {
    // There are cases where some video assets have a click sound at the beginning when
    // using the strategy suggested at https://alemangui.github.io/ramp-to-value.
    // For avoiding that, if the gain change is set to immediate, this will avoid that
    // click sound
    gain.setValueAtTime(value, time);
  } else {
    // https://alemangui.github.io/ramp-to-value
    gain.setTargetAtTime(value, time, 0.015);
  }
}

export function rampToGainAtTime(
  gain: AudioParam,
  startValue: number,
  endValue: number,
  startSec: number,
  durationSec: number,
) {
  if (startSec < 0) return;
  gain.setValueAtTime(startValue, startSec);
  gain.linearRampToValueAtTime(endValue, startSec + durationSec);
}

export function sourceDuration(
  source: Pick<IAudioSource, 'segments' | 'buffers'>,
) {
  const { segments } = source ?? {};

  if (!segments) {
    // Fall back to buffer duration when segments are not available
    const { buffers = [] } = source ?? {};
    return buffers.reduce((duration, buffer) => duration + buffer.duration, 0);
  }

  return asArray(segments).reduce(
    (duration, segment) => duration + segment.endSec - segment.startSec,
    0,
  );
}
